import { useState, useEffect, useCallback } from 'react';
import { Factory, Plus, Search, Edit2, Trash2, Building2, Globe, Phone, Mail, FileText, Eye } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '@/components/ui/alert-dialog';
import { useToast } from '@/hooks/use-toast';
import { Toaster } from '@/components/ui/toaster';
import { manufacturersAPI } from '@/services/api';

const ManufacturersPage = () => {
  const { toast } = useToast();
  const [manufacturers, setManufacturers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  
  // Dialog states
  const [dialogOpen, setDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedManufacturer, setSelectedManufacturer] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Metadata
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  
  // Form state
  const [formData, setFormData] = useState({
    company_name: '',
    type: 'manufacturer',
    country: 'IN',
    state: '',
    district: '',
    city: '',
    address: '',
    pincode: '',
    contact_number_1: '',
    contact_number_2: '',
    email: '',
    website: '',
    ie_code: '',
    gst_tax_id: '',
    notes: ''
  });

  const fetchManufacturers = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (searchTerm) params.search = searchTerm;
      if (typeFilter !== 'all') params.type = typeFilter;
      
      const response = await manufacturersAPI.list(params);
      setManufacturers(response.data || []);
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to fetch manufacturers', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [searchTerm, typeFilter, toast]);

  const fetchCountries = async () => {
    try {
      const response = await manufacturersAPI.getCountries();
      setCountries(response.data || []);
    } catch (error) {
      console.error('Failed to fetch countries:', error);
    }
  };

  const fetchStates = async (countryCode) => {
    try {
      const response = await manufacturersAPI.getStates(countryCode);
      setStates(response.data || []);
    } catch (error) {
      console.error('Failed to fetch states:', error);
      setStates([]);
    }
  };

  useEffect(() => {
    fetchManufacturers();
    fetchCountries();
  }, [fetchManufacturers]);

  useEffect(() => {
    if (formData.country) {
      fetchStates(formData.country);
    }
  }, [formData.country]);

  const resetForm = () => {
    setFormData({
      company_name: '',
      type: 'manufacturer',
      country: 'IN',
      state: '',
      district: '',
      city: '',
      address: '',
      pincode: '',
      contact_number_1: '',
      contact_number_2: '',
      email: '',
      website: '',
      ie_code: '',
      gst_tax_id: '',
      notes: ''
    });
    setSelectedManufacturer(null);
  };

  const handleAdd = () => {
    resetForm();
    setDialogOpen(true);
  };

  const handleEdit = (manufacturer) => {
    setSelectedManufacturer(manufacturer);
    setFormData({
      company_name: manufacturer.company_name || '',
      type: manufacturer.type || 'manufacturer',
      country: manufacturer.country || 'IN',
      state: manufacturer.state || '',
      district: manufacturer.district || '',
      city: manufacturer.city || '',
      address: manufacturer.address || '',
      pincode: manufacturer.pincode || '',
      contact_number_1: manufacturer.contact_number_1 || '',
      contact_number_2: manufacturer.contact_number_2 || '',
      email: manufacturer.email || '',
      website: manufacturer.website || '',
      ie_code: manufacturer.ie_code || '',
      gst_tax_id: manufacturer.gst_tax_id || '',
      notes: manufacturer.notes || ''
    });
    setDialogOpen(true);
  };

  const handleView = async (manufacturer) => {
    try {
      const response = await manufacturersAPI.get(manufacturer.id);
      setSelectedManufacturer(response.data);
      setViewDialogOpen(true);
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to fetch manufacturer details', variant: 'destructive' });
    }
  };

  const handleSave = async () => {
    if (!formData.company_name || !formData.contact_number_1 || !formData.country || !formData.state) {
      toast({ title: 'Validation Error', description: 'Please fill all required fields', variant: 'destructive' });
      return;
    }

    setSaving(true);
    try {
      if (selectedManufacturer) {
        await manufacturersAPI.update(selectedManufacturer.id, formData);
        toast({ title: 'Success', description: 'Manufacturer updated successfully' });
      } else {
        await manufacturersAPI.create(formData);
        toast({ title: 'Success', description: 'Manufacturer created successfully' });
      }
      setDialogOpen(false);
      fetchManufacturers();
    } catch (error) {
      toast({ 
        title: 'Error', 
        description: error.response?.data?.detail || 'Failed to save manufacturer', 
        variant: 'destructive' 
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await manufacturersAPI.delete(selectedManufacturer.id);
      toast({ title: 'Success', description: 'Manufacturer deactivated successfully' });
      setDeleteDialogOpen(false);
      fetchManufacturers();
    } catch (error) {
      toast({ 
        title: 'Error', 
        description: error.response?.data?.detail || 'Failed to deactivate manufacturer', 
        variant: 'destructive' 
      });
    }
  };

  return (
    <div className="p-6 space-y-6" data-testid="manufacturers-page">
      <Toaster />
      
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-indigo-100 rounded-lg">
            <Factory className="w-6 h-6 text-indigo-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-800">Manufacturers & Importers</h1>
            <p className="text-gray-500 text-sm">Manage manufacturers and importers for brands</p>
          </div>
        </div>
        <Button onClick={handleAdd} className="bg-indigo-600 hover:bg-indigo-700" data-testid="add-manufacturer-btn">
          <Plus className="w-4 h-4 mr-2" /> Add Manufacturer
        </Button>
      </div>

      {/* Filters */}
      <Card className="bg-white border-gray-200">
        <CardContent className="p-4">
          <div className="flex gap-4 items-center">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Search by name, GST, or IE Code..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 bg-gray-50 border-gray-300"
                data-testid="search-input"
              />
            </div>
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="w-48 bg-gray-50 border-gray-300">
                <SelectValue placeholder="Filter by type" />
              </SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="manufacturer">Manufacturers</SelectItem>
                <SelectItem value="importer">Importers</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="bg-white border-gray-200">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
            </div>
          ) : manufacturers.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-gray-500">
              <Factory className="w-12 h-12 mb-4 text-gray-300" />
              <p className="text-lg font-medium">No Manufacturers Found</p>
              <p className="text-sm">Add your first manufacturer to get started</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-50">
                  <TableHead className="text-gray-600">Company Name</TableHead>
                  <TableHead className="text-gray-600">Type</TableHead>
                  <TableHead className="text-gray-600">Location</TableHead>
                  <TableHead className="text-gray-600">Contact</TableHead>
                  <TableHead className="text-gray-600">GST/Tax ID</TableHead>
                  <TableHead className="text-gray-600">Brands</TableHead>
                  <TableHead className="text-gray-600">Pending</TableHead>
                  <TableHead className="text-gray-600 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {manufacturers.map((mfr) => (
                  <TableRow key={mfr.id} className="border-gray-200 hover:bg-gray-50">
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Building2 className="w-4 h-4 text-gray-400" />
                        <div>
                          <p className="font-medium text-gray-800">{mfr.company_name}</p>
                          {mfr.ie_code && <p className="text-xs text-gray-500">IE: {mfr.ie_code}</p>}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge className={mfr.type === 'manufacturer' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'}>
                        {mfr.type === 'manufacturer' ? 'Manufacturer' : 'Importer'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1 text-gray-600">
                        <Globe className="w-3 h-3" />
                        <span className="text-sm">{mfr.city || mfr.state}, {mfr.country}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1 text-gray-600">
                        <Phone className="w-3 h-3" />
                        <span className="text-sm">{mfr.contact_number_1}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm text-gray-600">{mfr.gst_tax_id || '-'}</span>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="bg-gray-50">
                        {mfr.total_brands || 0} Brands
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {mfr.total_pending_amount > 0 ? (
                        <span className="text-red-600 font-medium">₹{mfr.total_pending_amount.toLocaleString()}</span>
                      ) : (
                        <span className="text-green-600">₹0</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleView(mfr)}>
                          <Eye className="w-4 h-4 text-gray-500" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleEdit(mfr)}>
                          <Edit2 className="w-4 h-4 text-gray-500" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => { setSelectedManufacturer(mfr); setDeleteDialogOpen(true); }}>
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-3xl bg-white max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-gray-800">
              <Factory className="w-5 h-5 text-indigo-600" />
              {selectedManufacturer ? 'Edit Manufacturer' : 'Add New Manufacturer'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="grid grid-cols-2 gap-4 py-4">
            {/* Company Name */}
            <div className="space-y-2">
              <Label className="text-gray-700">Company Name *</Label>
              <Input
                value={formData.company_name}
                onChange={(e) => setFormData(p => ({ ...p, company_name: e.target.value }))}
                placeholder="Enter company name"
                className="bg-gray-50 border-gray-300"
                data-testid="company-name-input"
              />
            </div>
            
            {/* Type */}
            <div className="space-y-2">
              <Label className="text-gray-700">Type *</Label>
              <Select value={formData.type} onValueChange={(v) => setFormData(p => ({ ...p, type: v }))}>
                <SelectTrigger className="bg-gray-50 border-gray-300">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="manufacturer">Manufacturer</SelectItem>
                  <SelectItem value="importer">Importer</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/* Country */}
            <div className="space-y-2">
              <Label className="text-gray-700">Country *</Label>
              <Select value={formData.country} onValueChange={(v) => setFormData(p => ({ ...p, country: v, state: '' }))}>
                <SelectTrigger className="bg-gray-50 border-gray-300">
                  <SelectValue placeholder="Select country" />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  {countries.map(c => (
                    <SelectItem key={c.code} value={c.code}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* State */}
            <div className="space-y-2">
              <Label className="text-gray-700">State *</Label>
              <Select value={formData.state} onValueChange={(v) => setFormData(p => ({ ...p, state: v }))}>
                <SelectTrigger className="bg-gray-50 border-gray-300">
                  <SelectValue placeholder="Select state" />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  {states.map(s => (
                    <SelectItem key={s.code} value={s.code}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* District */}
            <div className="space-y-2">
              <Label className="text-gray-700">District</Label>
              <Input
                value={formData.district}
                onChange={(e) => setFormData(p => ({ ...p, district: e.target.value }))}
                placeholder="Enter district"
                className="bg-gray-50 border-gray-300"
              />
            </div>
            
            {/* City */}
            <div className="space-y-2">
              <Label className="text-gray-700">City</Label>
              <Input
                value={formData.city}
                onChange={(e) => setFormData(p => ({ ...p, city: e.target.value }))}
                placeholder="Enter city"
                className="bg-gray-50 border-gray-300"
              />
            </div>
            
            {/* Address */}
            <div className="col-span-2 space-y-2">
              <Label className="text-gray-700">Address</Label>
              <Textarea
                value={formData.address}
                onChange={(e) => setFormData(p => ({ ...p, address: e.target.value }))}
                placeholder="Enter full address"
                className="bg-gray-50 border-gray-300"
                rows={2}
              />
            </div>
            
            {/* Pincode */}
            <div className="space-y-2">
              <Label className="text-gray-700">Pincode</Label>
              <Input
                value={formData.pincode}
                onChange={(e) => setFormData(p => ({ ...p, pincode: e.target.value }))}
                placeholder="Enter pincode"
                className="bg-gray-50 border-gray-300"
              />
            </div>
            
            {/* Contact 1 */}
            <div className="space-y-2">
              <Label className="text-gray-700">Contact Number 1 *</Label>
              <Input
                value={formData.contact_number_1}
                onChange={(e) => setFormData(p => ({ ...p, contact_number_1: e.target.value }))}
                placeholder="Primary contact"
                className="bg-gray-50 border-gray-300"
                data-testid="contact1-input"
              />
            </div>
            
            {/* Contact 2 */}
            <div className="space-y-2">
              <Label className="text-gray-700">Contact Number 2</Label>
              <Input
                value={formData.contact_number_2}
                onChange={(e) => setFormData(p => ({ ...p, contact_number_2: e.target.value }))}
                placeholder="Alternate contact"
                className="bg-gray-50 border-gray-300"
              />
            </div>
            
            {/* Email */}
            <div className="space-y-2">
              <Label className="text-gray-700">Email</Label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData(p => ({ ...p, email: e.target.value }))}
                placeholder="company@example.com"
                className="bg-gray-50 border-gray-300"
              />
            </div>
            
            {/* Website */}
            <div className="space-y-2">
              <Label className="text-gray-700">Website</Label>
              <Input
                value={formData.website}
                onChange={(e) => setFormData(p => ({ ...p, website: e.target.value }))}
                placeholder="https://www.example.com"
                className="bg-gray-50 border-gray-300"
              />
            </div>
            
            {/* IE Code */}
            <div className="space-y-2">
              <Label className="text-gray-700">IE Code (Import/Export)</Label>
              <Input
                value={formData.ie_code}
                onChange={(e) => setFormData(p => ({ ...p, ie_code: e.target.value }))}
                placeholder="Import/Export code"
                className="bg-gray-50 border-gray-300"
              />
            </div>
            
            {/* GST/Tax ID */}
            <div className="space-y-2">
              <Label className="text-gray-700">GST / Tax ID</Label>
              <Input
                value={formData.gst_tax_id}
                onChange={(e) => setFormData(p => ({ ...p, gst_tax_id: e.target.value }))}
                placeholder="GST or Tax identification number"
                className="bg-gray-50 border-gray-300"
              />
            </div>
            
            {/* Notes */}
            <div className="col-span-2 space-y-2">
              <Label className="text-gray-700">Notes</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData(p => ({ ...p, notes: e.target.value }))}
                placeholder="Additional notes..."
                className="bg-gray-50 border-gray-300"
                rows={2}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="border-gray-300">
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700" data-testid="save-manufacturer-btn">
              {saving ? 'Saving...' : selectedManufacturer ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="max-w-2xl bg-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-gray-800">
              <Building2 className="w-5 h-5 text-indigo-600" />
              {selectedManufacturer?.company_name}
            </DialogTitle>
          </DialogHeader>
          
          {selectedManufacturer && (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500">Type</p>
                  <Badge className={selectedManufacturer.type === 'manufacturer' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'}>
                    {selectedManufacturer.type === 'manufacturer' ? 'Manufacturer' : 'Importer'}
                  </Badge>
                </div>
                <div>
                  <p className="text-sm text-gray-500">GST/Tax ID</p>
                  <p className="font-medium">{selectedManufacturer.gst_tax_id || '-'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">IE Code</p>
                  <p className="font-medium">{selectedManufacturer.ie_code || '-'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Location</p>
                  <p className="font-medium">{selectedManufacturer.city}, {selectedManufacturer.state}, {selectedManufacturer.country}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Contact</p>
                  <p className="font-medium">{selectedManufacturer.contact_number_1}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Email</p>
                  <p className="font-medium">{selectedManufacturer.email || '-'}</p>
                </div>
              </div>
              
              {selectedManufacturer.brands?.length > 0 && (
                <div>
                  <p className="text-sm text-gray-500 mb-2">Associated Brands</p>
                  <div className="flex flex-wrap gap-2">
                    {selectedManufacturer.brands.map(brand => (
                      <Badge key={brand.id} variant="outline" className="bg-gray-50">
                        {brand.name}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
              
              <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                <span className="text-gray-600">Total Pending Amount</span>
                <span className={`text-xl font-bold ${selectedManufacturer.total_pending_amount > 0 ? 'text-red-600' : 'text-green-600'}`}>
                  ₹{(selectedManufacturer.total_pending_amount || 0).toLocaleString()}
                </span>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Deactivate Manufacturer?</AlertDialogTitle>
            <AlertDialogDescription>
              This will deactivate "{selectedManufacturer?.company_name}". You can reactivate it later if needed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-gray-300">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Deactivate
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ManufacturersPage;
